<template>
  <v-card class="history">
    <v-card-text class="history-card-text">
      <v-timeline
        align-top
        dense
        v-if="panelOptionsDefinition.item.id && historyEntries && historyEntries.length > 0">
        <v-timeline-item
          v-for="(entry, i) in historyEntries"
          :key="i"
          :icon="actionIcon(entry)"
          :color="actionColor(entry)">
          <v-card class="elevation-2 single-comment">
            <v-layout
              justify-space-between
              fill-height
              v-bind="binding">
              <v-flex lg7>
                <div class="item-title">
                  <we-entity-to-avatar
                    :item="entry.user"
                    :schema="userSchema"
                    :size="avatarSize" />
                  <div class="user text-no-wrap">
                    <div class="text-no-wrap">
                      <strong>{{ actionName(entry) }}</strong> {{ entry.date | moment("from") }}
                      <span v-if="entry.entityDetails && entry.entityDetails.environment && entry.entityDetails.environment.label">
                        {{ $t('serverModel.components.history.label','on {label} environment', {label: entry.entityDetails.environment.label}) }}
                      </span>
                    </div>
                    {{ $t('serverModel.components.history.by','by') }}
                    <a
                      @click.stop="showUser(entry.user)"
                      v-if="entry.user">
                      {{ entry.user.login }}
                    </a>
                  </div>
                </div>
              </v-flex>
              <v-flex
                lg2
                class="center-on-row">
                <v-chip
                  v-if="entry.tag"
                  class="tag"
                  label
                  small
                >
                  {{ entry.tag }}
                </v-chip>
              </v-flex>
              <v-flex
                lg7
                class="align-right-on-row">
                <v-btn
                  v-if="entry.resourceFileName"
                  color="green"
                  :loading="downloadingExcel"
                  @click.stop="downloadExcel(entry)"
                  outline>
                  <v-icon
                    small
                    left>
                    fas fa-file-excel
                  </v-icon>
                  {{ $t('serverModel.components.history.downloadExcel','Download Excel') }}
                </v-btn>
                <v-btn
                  v-if="entry.resourceFileName"
                  color="orange"
                  :loading="downloadingResource"
                  @click.stop="downloadResource(entry)"
                  outline>
                  <v-icon
                    small
                    left>
                    far fa-file-archive
                  </v-icon>
                  {{ $t('serverModel.components.history.downloadZip','Download Zip') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card>
        </v-timeline-item>
      </v-timeline>
      <p v-else>
        {{ $t('serverModel.components.history.noHistory','This entity doesn\'t have history for now.') }}
      </p>
    </v-card-text>
  </v-card>
</template>

<script>
import { image } from 'faker'
import { mapState, mapActions } from 'vuex'
import { userSchema } from '../model'
import { historyDataSource } from '../services'
import { apiVersion, queryContentType } from '../constants'
import { actionIconMixin } from '../mixins/actionIcon'
import axios from 'axios'

export default {
  mixins: [actionIconMixin],
  data: () => ({
    avatarSize: 35,
    userSchema: userSchema,
    downloadingExcel: false,
    downloadingResource: false
  }),
  computed: {
    ...mapState('schema-view', [
      'panelOptionsDefinition'
    ]),
    ...mapState('authentication', [
      'instance', 'token'
    ]),
    entity () {
      return this.panelOptionsDefinition && this.panelOptionsDefinition.item
    },
    entitySchema () {
      return this.panelOptionsDefinition && this.panelOptionsDefinition.schema
    },
    historyEntries () {
      return this.historyResponse ? this.historyResponse.content : []
    },
    binding () { // Vuetify way to switch from a row-grid to a column-grid on breakpoint. From official doc.
      const binding = {}
      if (this.$vuetify.breakpoint.mdAndDown) {
        binding.column = true
      }
      return binding
    }
  },
  asyncComputed: {
    historyResponse () {
      return this.entitySchema
          && this.entity
          && historyDataSource.summary({
            argsUrl: `entry/${this.entitySchema.name.toLowerCase()}/${this.entity.id}`,
            contextApi: ''
          })
    }
  },
  methods: {
    ...mapActions('schema-view', ['displayDefinition']),
    ...mapActions('notification', ['error', 'notify']),
    avatar () {
      return image.avatar()
    },
    showUser (user) {
      this.displayDefinition({
        schema: userSchema,
        item: user,
        fetch: true
      })
    },
    async doDownload (entry, endpoint) {
      const response = await axios.get(`${this.instance}/history/entry/${entry.id}/${endpoint}`, {
        headers: {
          'Content-type': queryContentType,
          'Authorization': `Bearer ${this.token}`,
          'Accept': apiVersion
        },
        responseType: 'blob'
      })
      if (response.data) {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]))
        const fileLink = document.createElement('a')
        fileLink.href = fileURL

        let fileName = entry.resourceFileName
        if (response.headers && response.headers['content-disposition']) {
          const contentDisposition = response.headers['content-disposition']
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/)
          if (fileNameMatch.length === 2) {
            fileName = fileNameMatch[1]
          }
        }
        fileLink.setAttribute('download', fileName)

        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)

        this.notify(`${fileName} was downloaded successfully.`)
      }
    },
    async downloadResource (entry) {
      this.downloadingResource = true
      try {
        this.doDownload(entry, 'resource')
      } catch (error) {
        this.error(this.$t('serverModel.components.history.errors.downloadRessource', 'Couldn\'t download {filename} : {error}',
          { filename: entry.resourceFileName,
            error: (error && error.response && error.response.data && error.response.data.message)
              ? error.response.data.message
              : 'unknown'
          }))
      } finally {
        this.downloadingResource = false
      }
    },
    async downloadExcel (entry) {
      this.downloadingExcel = true
      try {
        this.doDownload(entry, 'excel')
      } catch (error) {
        this.$t('serverModel.components.history.errors.downloadExcel', 'Couldn\'t download Excel source : {error}',
          {
            error: (error && error.response && error.response.data && error.response.data.message)
              ? error.response.data.message
              : 'unknown'
          })
      } finally {
        this.downloadingExcel = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~common-assets/scss/we";

  .history {
    max-height: 100%;
    .history-card-text{
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: auto;
    }
    .item-title {
      font-size: 12px;
      @extend .text-no-wrap;
      @extend .flex-row;
      margin: 6px 8px;

      .user {
        margin-left: 10px;
      }
    }
    .layout {
      .center-on-row {
        text-align: center;
      }
      .align-right-on-row {
        text-align: right;
      }
    }
    .layout.column {
      .center-on-row, .align-right-on-row {
        text-align: left;
      }
    }

    .tag {
      @include text-no-wrap;
      max-width: 100%;
      margin: 12px 6px;
    }
    /deep/ .v-icon {
      font-size: 20px;
    }
  }
</style>
